import { inject } from '@angular/core';
import { catchError, first, map, Observable, of, switchMap } from 'rxjs';
import { DATABASE_NAME } from 'src/app/config/app.config';
import { STORE_DATABASE } from 'src/app/core/enums/storage/store-database.enum';
import { Logger } from 'src/app/core/interfaces/Logger';
import { IndexedDBRepository } from 'src/app/shared/interface/indexed-db-storage.model';
import { ACCOUNT_ERROR } from '../../domain/enum/error/error-accounts.enum';
import { Account } from '../../domain/model/account.model';
import { IAccountStorageRepository } from '../../domain/repositories/account-storage.repository';
import { AccountStorageMapper } from '../mappers/account-storage.mapper';
import { AccountFactoryService } from '../../account-factory.service';
import { IAccount } from '../interfaces/account-storage.interface';

export class AccountStorageAdapter extends IndexedDBRepository<IAccount> implements IAccountStorageRepository{
  private readonly logger = inject(Logger);
  private readonly accountFactoryService = inject(AccountFactoryService);

  readonly dbName = DATABASE_NAME;
  readonly instanceName = STORE_DATABASE.accounts;

  getAll(): Observable<Account[]> {
    return this.getItems().pipe(
      first(),
      switchMap((accounts: IAccount[]) =>
        this.accountFactoryService.createMany(accounts)
      ),
      catchError((error) => {
        const errorMessage = `${ACCOUNT_ERROR.getAccountsData}: ${error}`;

        this.logger.error(
          new Error('[AccountStorageRepository][getAll] ' + errorMessage),
          error
        );

        return of([]);
      })
    );
  }

  getById(id: string): Observable<Account | null> {
    return this.getItem(id).pipe(
      first(),
      switchMap((account: IAccount | null) => {
        if (account) {
          return this.accountFactoryService.createOne(account, true);
        }

        return of(null);
      }),
      catchError((error) => {
        this.logger.error(
          new Error(
            `[AccountStorageRepository][getById] Error fetching account with ID ${id}`
          ),
          error
        );

        return of(null);
      })
    );
  }

  save(accounts: Account[]): Observable<Account[]> {
    if (accounts.length) {
      const uniqueAccounts = Array.from(
        new Map(accounts.map(account => [account?.id, account])).values()
      );

      const localForageAccounts = uniqueAccounts.map((account) => ({
        key: account?.id,
        value: AccountStorageMapper.fromEntity(account),
      }));

      return this.setItems(localForageAccounts).pipe(
        first(),
        map(() => accounts),
        catchError((error) => {
          this.logger.error(
            new Error(
              `[AccountStorageRepository][save] Error inserting accounts`
            ),
            error
          );

          return of([]);
        })
      );
    }

    return of([]);
  }

  delete(accounts: Account[]): Observable<void> {
    return this.removeItems(accounts.map((account) => account?.id)).pipe(
      first(),
      catchError((error) => {
        this.logger.error(
          new Error(
            `[AccountStorageRepository][delete] Error deleting account with ID ${accounts
              .map((account) => account?.id)
              .join(', ')}`
          ),
          error
        );

        return of(undefined);
      })
    );
  }

  clear(): Observable<void> {
    return this.clearAll().pipe(
      first(),
      catchError((error) => {
        this.logger.error(
          new Error(
            '[AccountStorageRepository][clearAll] Error clearing all accounts'
          ),
          error
        );

        return of(undefined);
      })
    );
  }
}
